/**
 * Data layer for `blacklist-ip-block-anonymizer-block`. Thin typed fetch wrappers
 * over /api/v1/blacklist-ip-block-anonymizer-block. Raw `fetch()` is confined
 * here per the dash contract.
 *
 * BE shape (from apps/api/src/routes/blacklist-ip-block-anonymizer-block.ts):
 *   kind: 'ip' | 'cidr' | 'proxy' | 'tor' | 'vpn' | 'anonymous_all'
 *   value: IP literal | CIDR | proxy class | literal kind-name
 *   offer_id?: string | null
 *   note?: string | null
 *   created_at (epoch seconds), created_by?
 */

export const BLACKLIST_KINDS = [
	"ip",
	"cidr",
	"proxy",
	"tor",
	"vpn",
	"anonymous_all",
] as const;

export type BlacklistKind = (typeof BLACKLIST_KINDS)[number];

export const PROXY_CLASSES = ["http", "socks4", "socks5", "web"] as const;
export type ProxyClass = (typeof PROXY_CLASSES)[number];

export interface BlacklistRule {
	id: string;
	tenant_id: string;
	kind: BlacklistKind;
	value: string;
	offer_id: string | null;
	note: string | null;
	created_at: number;
	created_by: string | null;
}

export interface ListBlacklistResponse {
	items: BlacklistRule[];
	next_cursor: number | null;
}

export interface CreateBlacklistBody {
	kind: BlacklistKind;
	value: string;
	offer_id?: string | null;
	note?: string | null;
}

export interface CreateBlacklistResponse {
	ok: true;
	created: boolean;
	id: string;
	kind: BlacklistKind;
	value: string;
	offer_id: string | null;
}

export interface EvaluateBlacklistBody {
	ip?: string;
	offer_id?: string | null;
	flags?: {
		proxy?: ProxyClass;
		tor?: boolean;
		vpn?: boolean;
		anonymizer?: boolean;
	};
}

export interface EvaluateBlacklistResponse {
	blocked: boolean;
	rule?: {
		id: string;
		kind: BlacklistKind;
		value: string;
		offer_id: string | null;
	};
}

const BASE = "/api/v1/blacklist-ip-block-anonymizer-block";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(params?: {
	kind?: BlacklistKind;
	offer_id?: string | "none";
	limit?: number;
}): Promise<ListBlacklistResponse> {
	const qs = new URLSearchParams();
	if (params?.kind) qs.set("kind", params.kind);
	if (params?.offer_id) qs.set("offer_id", params.offer_id);
	if (params?.limit) qs.set("limit", String(params.limit));
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListBlacklistResponse>(res);
}

export async function create(
	body: CreateBlacklistBody,
): Promise<CreateBlacklistResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateBlacklistResponse>(res);
}

export async function remove(id: string): Promise<{ ok: true }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: true }>(res);
}

export async function evaluate(
	body: EvaluateBlacklistBody,
): Promise<EvaluateBlacklistResponse> {
	const res = await fetch(`${BASE}/evaluate`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<EvaluateBlacklistResponse>(res);
}

export const blacklistIpBlockAnonymizerBlockQueryKeys = {
	all: ["blacklist-ip-block-anonymizer-block"] as const,
	list: () => [...blacklistIpBlockAnonymizerBlockQueryKeys.all, "list"] as const,
	detail: (id: string) =>
		[...blacklistIpBlockAnonymizerBlockQueryKeys.all, id] as const,
};

// --- pure validators / classifiers (exported for unit tests) --------------

const IPV4_RE =
	/^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
const IPV4_CIDR_RE =
	/^((?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d))\/([0-9]|[12]\d|3[0-2])$/;

export function isIpv4(s: string): boolean {
	return IPV4_RE.test(s);
}

function ipv4ToUint32(ip: string): number | null {
	if (!IPV4_RE.test(ip)) return null;
	const parts = ip.split(".").map((n) => Number.parseInt(n, 10));
	return (
		(((parts[0] << 24) >>> 0) +
			(parts[1] << 16) +
			(parts[2] << 8) +
			parts[3]) >>>
		0
	);
}

export function parseCidrV4(
	cidr: string,
): { net: number; mask: number; prefix: number } | null {
	const m = cidr.match(IPV4_CIDR_RE);
	if (!m) return null;
	const net = ipv4ToUint32(m[1]);
	if (net === null) return null;
	const prefix = Number.parseInt(m[2], 10);
	if (prefix < 0 || prefix > 32) return null;
	const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
	return { net: (net & mask) >>> 0, mask, prefix };
}

export function cidrContainsV4(cidr: string, ip: string): boolean {
	const parsed = parseCidrV4(cidr);
	if (!parsed) return false;
	const ipU = ipv4ToUint32(ip);
	if (ipU === null) return false;
	return ((ipU & parsed.mask) >>> 0) === parsed.net;
}

/**
 * Local simulation of BE evaluation: first-match-wins against given rules.
 * Used for client-side test probes without a round-trip.
 */
export function simulateEvaluate(
	rules: BlacklistRule[],
	probe: EvaluateBlacklistBody,
): EvaluateBlacklistResponse {
	const ip = (probe.ip ?? "").trim().toLowerCase();
	const flags = probe.flags ?? {};
	const fTor = flags.tor === true;
	const fVpn = flags.vpn === true;
	const fAnon = flags.anonymizer === true;
	const fProxy =
		typeof flags.proxy === "string" &&
		(PROXY_CLASSES as readonly string[]).includes(flags.proxy)
			? flags.proxy
			: null;
	const anyAnon = fTor || fVpn || fAnon || fProxy !== null;
	for (const r of rules) {
		if (r.kind === "anonymous_all" && anyAnon) return { blocked: true, rule: pickRule(r) };
		if (r.kind === "tor" && fTor) return { blocked: true, rule: pickRule(r) };
		if (r.kind === "vpn" && fVpn) return { blocked: true, rule: pickRule(r) };
		if (r.kind === "proxy" && fProxy && r.value === fProxy)
			return { blocked: true, rule: pickRule(r) };
		if (r.kind === "ip" && r.value === ip)
			return { blocked: true, rule: pickRule(r) };
		if (r.kind === "cidr" && ip && isIpv4(ip) && cidrContainsV4(r.value, ip))
			return { blocked: true, rule: pickRule(r) };
	}
	return { blocked: false };
}

function pickRule(r: BlacklistRule) {
	return { id: r.id, kind: r.kind, value: r.value, offer_id: r.offer_id };
}

export function labelForKind(k: BlacklistKind): string {
	switch (k) {
		case "ip":
			return "IP literal";
		case "cidr":
			return "CIDR range";
		case "proxy":
			return "Proxy class";
		case "tor":
			return "Tor exit";
		case "vpn":
			return "VPN";
		case "anonymous_all":
			return "Anonymizer (all)";
	}
}

export function sourceForRule(r: BlacklistRule): "manual" | "anonymizer" | "auto" {
	if (r.kind === "anonymous_all") return "anonymizer";
	if (r.created_by === null) return "auto";
	return "manual";
}
